import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { HelpCircle, CreditCard, Building2, Check, ArrowRight, Receipt } from 'lucide-react';

export const ProductMockupsSection = () => {
  const { isDark } = useTheme();

  return (
    <section
      id="product"
      className={`section-apple px-6 ${isDark ? 'bg-[#0F0F1A]' : 'bg-[#F8F7F4]'}`}
      data-testid="product-mockups-section"
    >
      <div className="max-w-[1200px] mx-auto">
        {/* Title */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[13px] tracking-[0.02em] uppercase mb-4 text-[#81d8d0]">
            Aperçu produit
          </p>
          <h2
            className={`text-[40px] sm:text-[48px] font-semibold tracking-[-0.03em] leading-[1.1] mb-4 ${
              isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
            }`}
          >
            Trois écrans,
            <br />
            <span className="text-[#81d8d0]">trois gains concrets.</span>
          </h2>
          <p
            className={`text-[17px] max-w-[640px] mx-auto ${
              isDark ? 'text-[#86868b]' : 'text-[#6e6e73]'
            }`}
          >
            Pas de promesse vague. Voici exactement ce que tu vois dans Skipya.
          </p>
        </motion.div>

        {/* Mockup 1 — Aides détectées (large) */}
        <Mockup
          isDark={isDark}
          delay={0.1}
          label="01 — Aides"
          title="Tes aides détectées, en clair."
          desc="Skipya analyse ton profil et te dit ce à quoi tu peux prétendre, montant estimé inclus."
          testId="mockup-aides"
        >
          <AidesPreview isDark={isDark} />
        </Mockup>

        {/* Two side-by-side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <Mockup
            isDark={isDark}
            delay={0.2}
            label="02 — Abonnements"
            title="Tous tes abonnements suivis."
            desc="Coût total mensuel, détection des doublons et économies potentielles."
            testId="mockup-abonnements"
            compact
          >
            <AbonnementsPreview isDark={isDark} />
          </Mockup>

          <Mockup
            isDark={isDark}
            delay={0.3}
            label="03 — Organismes"
            title="Tes organismes à portée."
            desc="Contacts, références client et mails pré-rédigés."
            testId="mockup-organismes"
            compact
          >
            <OrganismesPreview isDark={isDark} />
          </Mockup>
        </div>
      </div>
    </section>
  );
};

const Mockup = ({ isDark, delay, label, title, desc, children, testId, compact }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.05 }}
    transition={{ duration: 0.5, delay }}
    className={`rounded-3xl overflow-hidden border ${
      isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-black/5'
    }`}
    data-testid={testId}
  >
    <div className={`p-6 sm:p-8 ${compact ? '' : 'lg:flex lg:items-center lg:gap-10'}`}>
      <div className={compact ? 'mb-5' : 'lg:w-1/2 lg:pr-6 mb-6 lg:mb-0'}>
        <p className="text-[11px] uppercase tracking-widest text-[#81d8d0] mb-3">{label}</p>
        <h3
          className={`text-[24px] sm:text-[28px] font-semibold tracking-[-0.02em] leading-[1.15] mb-3 ${
            isDark ? 'text-[#f5f5f7]' : 'text-[#1d1d1f]'
          }`}
        >
          {title}
        </h3>
        <p className={`text-[14px] leading-[1.5] ${isDark ? 'text-[#86868b]' : 'text-[#6e6e73]'}`}>
          {desc}
        </p>
      </div>
      <div className={compact ? '' : 'lg:w-1/2'}>{children}</div>
    </div>
  </motion.div>
);

// --------- Preview: Aides ---------

const AidesPreview = ({ isDark }) => {
  const aides = [
    { name: 'APL', amount: 142, eligible: true, icon: '🏠' },
    { name: 'Prime d\'activité', amount: 86, eligible: true, icon: '💼' },
    { name: 'Chèque énergie', amount: 48, eligible: true, icon: '⚡' },
    { name: 'CSS (santé)', amount: null, eligible: 'maybe', icon: '🏥' },
  ];
  const total = aides.reduce((s, a) => s + (a.eligible === true ? a.amount : 0), 0);

  return (
    <div className={`rounded-2xl overflow-hidden border ${isDark ? 'bg-black/40 border-white/5' : 'bg-[#f8f7f4] border-black/5'}`}>
      {/* Toolbar */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-white/5' : 'border-black/5'}`}>
        <div className="flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-[#81d8d0]" />
          <span className="text-[13px] font-medium">Mes aides</span>
        </div>
        <span className="text-[11px] opacity-60">Profil : alternant, célibataire</span>
      </div>

      {/* Total card */}
      <div className="p-5">
        <div className="text-[11px] uppercase tracking-widest opacity-60 mb-1">Estimation mensuelle</div>
        <div className="text-[36px] font-light text-[#81d8d0]">{total} €<span className="text-base opacity-50">/mois</span></div>
        <div className="text-[11px] opacity-60">soit ~{total * 12} € sur l'année</div>
      </div>

      {/* Items */}
      <div className="px-4 pb-4 space-y-2">
        {aides.map((a) => (
          <div
            key={a.name}
            className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}
          >
            <div className="flex items-center gap-2.5">
              <span className="text-lg">{a.icon}</span>
              <div>
                <div className="text-[13px] font-medium">{a.name}</div>
                <div className="text-[10px] opacity-60">
                  {a.eligible === true ? 'Éligible' : a.eligible === 'maybe' ? 'À vérifier' : 'Non éligible'}
                </div>
              </div>
            </div>
            <div className="text-right">
              {a.amount ? (
                <span className="text-[14px] font-medium text-[#81d8d0]">
                  +{a.amount} €
                </span>
              ) : (
                <span className="text-[11px] opacity-50">—</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className={`px-4 py-3 border-t ${isDark ? 'border-white/5' : 'border-black/5'} flex items-center justify-between`}>
        <span className="text-[11px] opacity-60">3 tâches préparées pour toi</span>
        <span className="text-[11px] text-[#81d8d0] flex items-center gap-1">
          Continuer <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </div>
  );
};

// --------- Preview: Abonnements ---------

const AbonnementsPreview = ({ isDark }) => {
  const subs = [
    { name: 'Netflix Standard', amount: 13.49, cat: '📺' },
    { name: 'Spotify Premium', amount: 10.99, cat: '🎵' },
    { name: 'Amazon Prime', amount: 6.99, cat: '📦' },
    { name: 'Disney+', amount: 8.99, cat: '📺', dup: true },
    { name: 'Salle de sport', amount: 29.99, cat: '🏋️' },
  ];
  const total = subs.reduce((s, x) => s + x.amount, 0);

  return (
    <div className={`rounded-2xl overflow-hidden border ${isDark ? 'bg-black/40 border-white/5' : 'bg-[#f8f7f4] border-black/5'}`}>
      <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-white/5' : 'border-black/5'}`}>
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-[#81d8d0]" />
          <span className="text-[13px] font-medium">Mes abonnements</span>
        </div>
        <span className="text-[11px] opacity-60">{subs.length} actifs</span>
      </div>

      <div className="p-4">
        <div className="flex items-baseline justify-between mb-3">
          <span className="text-[11px] uppercase tracking-widest opacity-60">Total mensuel</span>
          <span className="text-[20px] font-light">{total.toFixed(2)} €</span>
        </div>

        <div className="space-y-1.5">
          {subs.map((s, i) => (
            <div
              key={i}
              className={`flex items-center justify-between p-2 rounded-lg text-[12px] ${
                s.dup ? (isDark ? 'bg-amber-500/10' : 'bg-amber-50') : (isDark ? 'bg-white/5' : 'bg-white')
              }`}
            >
              <span className="flex items-center gap-2">
                <span>{s.cat}</span>
                <span className="font-medium">{s.name}</span>
                {s.dup && <span className="text-[9px] uppercase tracking-widest text-amber-500">doublon</span>}
              </span>
              <span className="opacity-70">{s.amount.toFixed(2)} €</span>
            </div>
          ))}
        </div>

        <div className={`mt-3 p-2.5 rounded-lg text-[11px] ${isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'} border`}>
          💡 1 doublon détecté — économise ~9 €/mois
        </div>
      </div>
    </div>
  );
};

// --------- Preview: Organismes ---------

const OrganismesPreview = ({ isDark }) => {
  const orgs = [
    { name: 'CAF', ref: 'N° 8472193', color: '#3b82f6' },
    { name: 'Impôts', ref: 'FR 1234 5678', color: '#ef4444' },
    { name: 'CPAM', ref: 'NIR 1·85·...', color: '#10b981' },
    { name: 'Free Mobile', ref: '0689...', color: '#f97316' },
  ];
  return (
    <div className={`rounded-2xl overflow-hidden border ${isDark ? 'bg-black/40 border-white/5' : 'bg-[#f8f7f4] border-black/5'}`}>
      <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-white/5' : 'border-black/5'}`}>
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[#81d8d0]" />
          <span className="text-[13px] font-medium">Mes organismes</span>
        </div>
        <span className="text-[11px] opacity-60">{orgs.length} suivis</span>
      </div>

      <div className="p-3 space-y-2">
        {orgs.map((o) => (
          <div
            key={o.name}
            className={`flex items-center justify-between p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}
          >
            <div className="flex items-center gap-2.5">
              <div className="w-2 h-2 rounded-full" style={{ background: o.color }} />
              <div>
                <div className="text-[13px] font-medium">{o.name}</div>
                <div className="text-[10px] opacity-60">{o.ref}</div>
              </div>
            </div>
            <span className="text-[11px] text-[#81d8d0]">Mail prêt</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductMockupsSection;
